/**
 * Shared password validation utility.
 *
 * Runtime-agnostic: works in both Node.js and the browser.
 * Used by:
 *   - Backend: InvitationsService, UsersMeService (password change)
 *   - Frontend: LoginComponent, RegisterComponent, AcceptInvitationComponent
 */

export interface PasswordPolicy {
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireDigit: boolean;
  requireSpecialChar: boolean;
}

export interface PasswordValidationResult {
  valid: boolean;
  errors: PasswordValidationError[];
}

export type PasswordValidationError =
  | 'too_short'
  | 'too_long'
  | 'missing_uppercase'
  | 'missing_lowercase'
  | 'missing_digit'
  | 'missing_special_char';

/**
 * Validate a password against a policy.
 */
export function validatePassword(password: string, policy: PasswordPolicy): PasswordValidationResult {
  const errors: PasswordValidationError[] = [];

  if (password.length < policy.minLength) errors.push('too_short');
  if (password.length > policy.maxLength) errors.push('too_long');
  if (policy.requireUppercase && !/[A-Z]/.test(password)) errors.push('missing_uppercase');
  if (policy.requireLowercase && !/[a-z]/.test(password)) errors.push('missing_lowercase');
  if (policy.requireDigit && !/\d/.test(password)) errors.push('missing_digit');
  if (policy.requireSpecialChar && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
    errors.push('missing_special_char');
  }

  return { valid: errors.length === 0, errors };
}

/** Default policy for when tenant config is unavailable. */
export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 8,
  maxLength: 128,
  requireUppercase: false,
  requireLowercase: false,
  requireDigit: false,
  requireSpecialChar: false,
};
